import { useQuery } from '@tanstack/react-query';
import { endpoints } from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Gamepad2, Cpu, MemoryStick, HardDrive } from 'lucide-react';
import { formatBytes, formatPercent } from '@/lib/format';
import { StatusBadge, StatusIcon } from '@/components/ui/StatusBadge';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { useNow } from '@/lib/hooks';

const REFETCH = 10_000;

export function PelicanWidget() {
  const now = useNow(1_000);
  const q = useQuery({
    queryKey: ['pelican'],
    queryFn: endpoints.integrations.pelican.stats,
    refetchInterval: REFETCH,
  });
  const data = q.data?.pelican;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Gamepad2 className="h-3.5 w-3.5" />
          Minecraft servers
        </CardTitle>
        {data && (
          <span className="text-xs text-muted-foreground">
            {data.runningServers}/{data.totalServers} running
          </span>
        )}
      </CardHeader>
      <CardBody>
        {!data ? (
          <div className="text-sm text-muted-foreground">
            {q.isLoading ? 'loading…' : 'unavailable (check Pelican panel)'}
          </div>
        ) : data.servers.length === 0 ? (
          <div className="text-sm text-muted-foreground">no servers</div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2 text-xs">
              <Tile
                label="Total RAM"
                value={formatBytes(data.totalRamMb * 1024 * 1024)}
                icon={MemoryStick}
              />
              <Tile
                label="CPU sum"
                value={formatPercent(data.totalCpuPercent, 0)}
                icon={Cpu}
              />
              <Tile
                label="Disk"
                value={formatBytes(data.totalDiskMb * 1024 * 1024)}
                icon={HardDrive}
              />
            </div>
            <ul className="space-y-1.5">
              {data.servers.map((s) => {
                const ramPct = s.limits.memoryMb > 0 ? (s.resources.memoryMb / s.limits.memoryMb) * 100 : 0;
                return (
                  <li
                    key={s.identifier || s.uuid || s.name}
                    className="rounded-md border border-border bg-card/60 p-2 text-xs"
                  >
                    <a href={`/integrations/pelican/${s.identifier || s.uuid}`} className="block">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <StatusIcon status={s.status} />
                          <span className="font-medium truncate">{s.name}</span>
                        </div>
                        <StatusBadge status={s.status} showIcon={false} />
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-1.5">
                        <div>
                          <div className="flex justify-between text-[10px] mb-0.5">
                            <span className="text-muted-foreground/70">RAM</span>
                            <span className="tabular-nums">
                              {formatBytes(s.resources.memoryMb * 1024 * 1024)}
                            </span>
                          </div>
                          <ProgressBar value={ramPct} />
                        </div>
                        <div className="flex items-center justify-between text-[10px]">
                          <span className="text-muted-foreground/70">CPU</span>
                          <span className="tabular-nums">{formatPercent(s.resources.cpuPercent, 0)}</span>
                        </div>
                      </div>
                    </a>
                  </li>
                );
              })}
            </ul>
            <div className="flex items-center justify-end text-xs text-muted-foreground border-t border-border pt-2">
              <span>{q.data?.lastUpdated ? relTime(q.data.lastUpdated, now) : '—'}</span>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function Tile({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon: typeof Cpu;
}) {
  return (
    <div className="rounded-md border border-border p-2">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div className="text-base font-semibold tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

function relTime(iso: string, now: number): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  const diff = Math.max(0, Math.floor((now - t) / 1000));
  if (diff === 0) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}
